/**
 * AudioData.ts
 * Definitions for music tracks and sound effects
 */

// =========================================================================
// Music Tracks
// =========================================================================

export enum AudioTrack {
  // Main themes
  TITLE = 'title',
  WORLD_MAP = 'world_map',
  TOWN = 'town',
  DUNGEON = 'dungeon',
  BATTLE = 'battle',
  BOSS_BATTLE = 'boss_battle',
  VICTORY = 'victory',
  GAME_OVER = 'game_over',

  // Special
  LEVEL_UP = 'level_up',
  CRYSTAL_RESTORED = 'crystal_restored',
  AIRSHIP = 'airship',
  SHIP = 'ship',
}

/**
 * Music track definitions with tempo and mood
 */
export interface MusicDefinition {
  track: AudioTrack;
  tempo: number; // BPM
  loop: boolean;
  mood: 'epic' | 'peaceful' | 'tense' | 'exciting' | 'victorious' | 'somber';
  duration?: number; // For non-looping tracks
}

export const MUSIC_DEFINITIONS: Record<AudioTrack, MusicDefinition> = {
  [AudioTrack.TITLE]: {
    track: AudioTrack.TITLE,
    tempo: 120,
    loop: true,
    mood: 'epic',
  },
  [AudioTrack.WORLD_MAP]: {
    track: AudioTrack.WORLD_MAP,
    tempo: 140,
    loop: true,
    mood: 'exciting',
  },
  [AudioTrack.TOWN]: {
    track: AudioTrack.TOWN,
    tempo: 100,
    loop: true,
    mood: 'peaceful',
  },
  [AudioTrack.DUNGEON]: {
    track: AudioTrack.DUNGEON,
    tempo: 90,
    loop: true,
    mood: 'tense',
  },
  [AudioTrack.BATTLE]: {
    track: AudioTrack.BATTLE,
    tempo: 160,
    loop: true,
    mood: 'exciting',
  },
  [AudioTrack.BOSS_BATTLE]: {
    track: AudioTrack.BOSS_BATTLE,
    tempo: 180,
    loop: true,
    mood: 'epic',
  },
  [AudioTrack.VICTORY]: {
    track: AudioTrack.VICTORY,
    tempo: 140,
    loop: false,
    mood: 'victorious',
    duration: 5000,
  },
  [AudioTrack.GAME_OVER]: {
    track: AudioTrack.GAME_OVER,
    tempo: 60,
    loop: false,
    mood: 'somber',
    duration: 8000,
  },
  [AudioTrack.LEVEL_UP]: {
    track: AudioTrack.LEVEL_UP,
    tempo: 120,
    loop: false,
    mood: 'victorious',
    duration: 3000,
  },
  [AudioTrack.CRYSTAL_RESTORED]: {
    track: AudioTrack.CRYSTAL_RESTORED,
    tempo: 100,
    loop: false,
    mood: 'epic',
    duration: 6000,
  },
  [AudioTrack.AIRSHIP]: {
    track: AudioTrack.AIRSHIP,
    tempo: 150,
    loop: true,
    mood: 'exciting',
  },
  [AudioTrack.SHIP]: {
    track: AudioTrack.SHIP,
    tempo: 110,
    loop: true,
    mood: 'peaceful',
  },
};

// =========================================================================
// Sound Effects
// =========================================================================

export enum SoundEffect {
  // Menu
  MENU_CURSOR = 'menu_cursor',
  MENU_SELECT = 'menu_select',
  MENU_CANCEL = 'menu_cancel',
  MENU_OPEN = 'menu_open',
  MENU_CLOSE = 'menu_close',
  ERROR = 'error',

  // Combat
  ATTACK_HIT = 'attack_hit',
  ATTACK_MISS = 'attack_miss',
  CRITICAL_HIT = 'critical_hit',
  ENEMY_HIT = 'enemy_hit',
  ENEMY_DEFEAT = 'enemy_defeat',

  // Spells by element
  SPELL_FIRE = 'spell_fire',
  SPELL_ICE = 'spell_ice',
  SPELL_LIGHTNING = 'spell_lightning',
  SPELL_HEAL = 'spell_heal',
  SPELL_BUFF = 'spell_buff',
  SPELL_DEBUFF = 'spell_debuff',

  // Items
  ITEM_USE = 'item_use',
  POTION_USE = 'potion_use',
  TREASURE_OPEN = 'treasure_open',
  DOOR_OPEN = 'door_open',

  // Progression
  LEVEL_UP_SFX = 'level_up_sfx',
  STAT_INCREASE = 'stat_increase',
  GOLD_RECEIVE = 'gold_receive',
  ITEM_RECEIVE = 'item_receive',

  // Movement
  FOOTSTEP = 'footstep',
  BUMP = 'bump',
  STAIRS = 'stairs',

  // Shop
  SHOP_BUY = 'shop_buy',
  SHOP_SELL = 'shop_sell',
  NOT_ENOUGH_GOLD = 'not_enough_gold',

  // Special
  SAVE = 'save',
  ENCOUNTER = 'encounter',
  FLEE_SUCCESS = 'flee_success',
  FLEE_FAIL = 'flee_fail',
}

/**
 * Sound effect definitions with wave parameters
 */
export interface SoundDefinition {
  effect: SoundEffect;
  type: 'sine' | 'square' | 'sawtooth' | 'triangle' | 'noise';
  frequency: number | number[]; // Single or array for sweep/sequence
  duration: number; // milliseconds
  volume: number; // 0-1
  envelope?: {
    attack: number; // milliseconds
    decay: number; // milliseconds
    sustain: number; // 0-1
    release: number; // milliseconds
  };
}

export const SOUND_DEFINITIONS: Record<SoundEffect, SoundDefinition> = {
  // Menu sounds - simple beeps
  [SoundEffect.MENU_CURSOR]: {
    effect: SoundEffect.MENU_CURSOR,
    type: 'square',
    frequency: 800,
    duration: 50,
    volume: 0.3,
  },
  [SoundEffect.MENU_SELECT]: {
    effect: SoundEffect.MENU_SELECT,
    type: 'square',
    frequency: [600, 1200],
    duration: 100,
    volume: 0.4,
  },
  [SoundEffect.MENU_CANCEL]: {
    effect: SoundEffect.MENU_CANCEL,
    type: 'square',
    frequency: [800, 400],
    duration: 100,
    volume: 0.4,
  },
  [SoundEffect.MENU_OPEN]: {
    effect: SoundEffect.MENU_OPEN,
    type: 'triangle',
    frequency: [400, 800],
    duration: 150,
    volume: 0.3,
  },
  [SoundEffect.MENU_CLOSE]: {
    effect: SoundEffect.MENU_CLOSE,
    type: 'triangle',
    frequency: [800, 400],
    duration: 150,
    volume: 0.3,
  },
  [SoundEffect.ERROR]: {
    effect: SoundEffect.ERROR,
    type: 'sawtooth',
    frequency: [200, 150],
    duration: 200,
    volume: 0.4,
  },

  // Combat sounds - percussive and impactful
  [SoundEffect.ATTACK_HIT]: {
    effect: SoundEffect.ATTACK_HIT,
    type: 'noise',
    frequency: 100,
    duration: 100,
    volume: 0.5,
    envelope: {
      attack: 5,
      decay: 30,
      sustain: 0.3,
      release: 65,
    },
  },
  [SoundEffect.ATTACK_MISS]: {
    effect: SoundEffect.ATTACK_MISS,
    type: 'sawtooth',
    frequency: [1000, 200],
    duration: 150,
    volume: 0.3,
  },
  [SoundEffect.CRITICAL_HIT]: {
    effect: SoundEffect.CRITICAL_HIT,
    type: 'noise',
    frequency: 150,
    duration: 150,
    volume: 0.7,
    envelope: {
      attack: 1,
      decay: 40,
      sustain: 0.4,
      release: 109,
    },
  },
  [SoundEffect.ENEMY_HIT]: {
    effect: SoundEffect.ENEMY_HIT,
    type: 'noise',
    frequency: 80,
    duration: 80,
    volume: 0.4,
    envelope: {
      attack: 5,
      decay: 25,
      sustain: 0.2,
      release: 50,
    },
  },
  [SoundEffect.ENEMY_DEFEAT]: {
    effect: SoundEffect.ENEMY_DEFEAT,
    type: 'sawtooth',
    frequency: [800, 100],
    duration: 500,
    volume: 0.5,
  },

  // Spell sounds - magical and ethereal
  [SoundEffect.SPELL_FIRE]: {
    effect: SoundEffect.SPELL_FIRE,
    type: 'sawtooth',
    frequency: [200, 600],
    duration: 300,
    volume: 0.5,
  },
  [SoundEffect.SPELL_ICE]: {
    effect: SoundEffect.SPELL_ICE,
    type: 'sine',
    frequency: [1200, 400],
    duration: 400,
    volume: 0.4,
  },
  [SoundEffect.SPELL_LIGHTNING]: {
    effect: SoundEffect.SPELL_LIGHTNING,
    type: 'noise',
    frequency: 200,
    duration: 250,
    volume: 0.6,
    envelope: {
      attack: 10,
      decay: 80,
      sustain: 0.3,
      release: 160,
    },
  },
  [SoundEffect.SPELL_HEAL]: {
    effect: SoundEffect.SPELL_HEAL,
    type: 'sine',
    frequency: [400, 800, 1200],
    duration: 500,
    volume: 0.4,
  },
  [SoundEffect.SPELL_BUFF]: {
    effect: SoundEffect.SPELL_BUFF,
    type: 'triangle',
    frequency: [600, 1000],
    duration: 300,
    volume: 0.4,
  },
  [SoundEffect.SPELL_DEBUFF]: {
    effect: SoundEffect.SPELL_DEBUFF,
    type: 'sawtooth',
    frequency: [600, 300],
    duration: 300,
    volume: 0.4,
  },

  // Item sounds
  [SoundEffect.ITEM_USE]: {
    effect: SoundEffect.ITEM_USE,
    type: 'triangle',
    frequency: [800, 1200],
    duration: 200,
    volume: 0.4,
  },
  [SoundEffect.POTION_USE]: {
    effect: SoundEffect.POTION_USE,
    type: 'sine',
    frequency: [400, 600, 800],
    duration: 300,
    volume: 0.4,
  },
  [SoundEffect.TREASURE_OPEN]: {
    effect: SoundEffect.TREASURE_OPEN,
    type: 'square',
    frequency: [400, 800, 1200, 1600],
    duration: 500,
    volume: 0.5,
  },
  [SoundEffect.DOOR_OPEN]: {
    effect: SoundEffect.DOOR_OPEN,
    type: 'sawtooth',
    frequency: [200, 400],
    duration: 300,
    volume: 0.3,
  },

  // Progression sounds
  [SoundEffect.LEVEL_UP_SFX]: {
    effect: SoundEffect.LEVEL_UP_SFX,
    type: 'square',
    frequency: [600, 800, 1000, 1200, 1400],
    duration: 800,
    volume: 0.5,
  },
  [SoundEffect.STAT_INCREASE]: {
    effect: SoundEffect.STAT_INCREASE,
    type: 'sine',
    frequency: [800, 1200],
    duration: 150,
    volume: 0.4,
  },
  [SoundEffect.GOLD_RECEIVE]: {
    effect: SoundEffect.GOLD_RECEIVE,
    type: 'triangle',
    frequency: [1000, 1400],
    duration: 200,
    volume: 0.4,
  },
  [SoundEffect.ITEM_RECEIVE]: {
    effect: SoundEffect.ITEM_RECEIVE,
    type: 'square',
    frequency: [800, 1200, 1600],
    duration: 400,
    volume: 0.4,
  },

  // Movement sounds
  [SoundEffect.FOOTSTEP]: {
    effect: SoundEffect.FOOTSTEP,
    type: 'noise',
    frequency: 100,
    duration: 50,
    volume: 0.2,
    envelope: {
      attack: 5,
      decay: 15,
      sustain: 0.1,
      release: 30,
    },
  },
  [SoundEffect.BUMP]: {
    effect: SoundEffect.BUMP,
    type: 'noise',
    frequency: 80,
    duration: 80,
    volume: 0.3,
  },
  [SoundEffect.STAIRS]: {
    effect: SoundEffect.STAIRS,
    type: 'triangle',
    frequency: [400, 600],
    duration: 200,
    volume: 0.3,
  },

  // Shop sounds
  [SoundEffect.SHOP_BUY]: {
    effect: SoundEffect.SHOP_BUY,
    type: 'triangle',
    frequency: [600, 1000, 1400],
    duration: 300,
    volume: 0.4,
  },
  [SoundEffect.SHOP_SELL]: {
    effect: SoundEffect.SHOP_SELL,
    type: 'triangle',
    frequency: [1000, 600],
    duration: 200,
    volume: 0.4,
  },
  [SoundEffect.NOT_ENOUGH_GOLD]: {
    effect: SoundEffect.NOT_ENOUGH_GOLD,
    type: 'sawtooth',
    frequency: [300, 200],
    duration: 300,
    volume: 0.4,
  },

  // Special sounds
  [SoundEffect.SAVE]: {
    effect: SoundEffect.SAVE,
    type: 'sine',
    frequency: [600, 800, 1000, 1200],
    duration: 600,
    volume: 0.5,
  },
  [SoundEffect.ENCOUNTER]: {
    effect: SoundEffect.ENCOUNTER,
    type: 'noise',
    frequency: 150,
    duration: 200,
    volume: 0.6,
    envelope: {
      attack: 20,
      decay: 60,
      sustain: 0.4,
      release: 120,
    },
  },
  [SoundEffect.FLEE_SUCCESS]: {
    effect: SoundEffect.FLEE_SUCCESS,
    type: 'triangle',
    frequency: [800, 1400],
    duration: 300,
    volume: 0.4,
  },
  [SoundEffect.FLEE_FAIL]: {
    effect: SoundEffect.FLEE_FAIL,
    type: 'sawtooth',
    frequency: [600, 300],
    duration: 300,
    volume: 0.4,
  },
};
